import { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Menu, X, ChevronDown, Film, MapPin, Sparkles, Briefcase } from "lucide-react";
import locations, { categories as locCategories } from "@/data/locations";

const R2 = "https://pub-626563a90a7f431c96f87c5838c120bc.r2.dev";

// Service-Vorschauen
const servicesNav = [
  { slug: "imagefilm",            title: "Imagefilm",        poster: "/thumbnails/imagefilm.jpg" },
  { slug: "recruiting",           title: "Recruiting",       poster: "/thumbnails/recruiting.jpg" },
  { slug: "social-media-content", title: "Social Media",     poster: "/thumbnails/social-media.jpg" },
  { slug: "hochzeitsvideo",       title: "Hochzeit",         poster: "/thumbnails/hochzeit.jpg" },
  { slug: "produktvideo",         title: "Produkt",          poster: "/thumbnails/produkt.jpg" },
  { slug: "event-city",           title: "Event & City",     poster: "/thumbnails/event.jpg" },
  { slug: "immobilien",           title: "Immobilien",       poster: "/thumbnails/roomtour-penthouse.webp" },
  { slug: "ki-video-generation",  title: "KI-Video",         poster: "/thumbnails/ki-commercial-elevator.webp" },
];

// Cases mit existierenden Thumbnails / Postern
const casesNav = [
  { slug: "maybach-landaulet-film",      title: "Maybach Landaulet",    poster: "/thumbnails/loc-hechingen.webp" },
  { slug: "geiger-group",                title: "Geiger Group",         poster: "/thumbnails/loc-pfullendorf.webp" },
  { slug: "dubai-reel-package",          title: "Dubai Luxury Cars",    poster: "/thumbnails/reel-2.webp" },
  { slug: "stadt-villingen-schwenningen", title: "Stadt VS Marketing",  poster: "/thumbnails/loc-villingen-schwenningen.webp" },
  { slug: "startup-foerderung-vs",       title: "Startup-Förderung VS", poster: "/thumbnails/01-maedchen-film01-16-9.webp" },
  { slug: "corporate-recruiting",        title: "Corporate Recruiting", poster: "/thumbnails/loc-moenchweiler.webp" },
  { slug: "seedos-product-campaign",     title: "Seedos Kampagne",      poster: "/thumbnails/seedos-allgemein.webp" },
];

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null); // 'portfolio' | 'drehorte' | 'services' | null
  const [mobileExpand, setMobileExpand] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const closeTimer = useRef(null);

  // Nav materialisiert sich beim Scrollen (transparent über dem Hero → Glas beim Scrollen)
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToSection = (id) => {
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 100);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
    setIsOpen(false);
    setOpenDropdown(null);
  };

  const closeAll = () => {
    setOpenDropdown(null);
    setIsOpen(false);
    setMobileExpand(null);
  };

  // Hover-mit-Verzögerung damit das Dropdown nicht zu früh schließt
  const openDropdownDelayed = (name) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpenDropdown(name);
  };
  const closeDropdownDelayed = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpenDropdown(null), 150);
  };

  // ESC schließt
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && closeAll();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const Pill = ({ children, active, onClick, hasCaret }) => (
    <button
      onClick={onClick}
      className={`flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
        active
          ? "text-[hsl(var(--accent))] bg-white/5"
          : "text-muted-foreground hover:text-[hsl(var(--accent))] hover:bg-white/5"
      }`}
    >
      {children}
      {hasCaret && (
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${active ? "rotate-180" : ""}`} />
      )}
    </button>
  );

  const ThumbCard = ({ to, title, poster, sub }) => (
    <Link
      to={to}
      onClick={closeAll}
      className="group flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 transition-colors"
    >
      <div className="w-14 h-10 rounded-md overflow-hidden bg-secondary/40 flex-shrink-0 border border-white/5">
        <img
          src={poster}
          alt=""
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
          onError={(e) => { e.currentTarget.style.opacity = '0.2'; }}
        />
      </div>
      <div className="min-w-0">
        <div className="text-sm font-medium text-white truncate group-hover:text-[hsl(var(--accent))] transition-colors">
          {title}
        </div>
        {sub && <div className="text-[11px] text-muted-foreground truncate">{sub}</div>}
      </div>
    </Link>
  );

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled || openDropdown || isOpen
          ? "bg-background/85 backdrop-blur-xl border-b border-white/10 shadow-lg shadow-black/30"
          : "bg-gradient-to-b from-background/70 to-transparent border-b border-transparent"
      }`}
      onMouseLeave={closeDropdownDelayed}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`flex items-center justify-between transition-all duration-500 ${scrolled ? "h-16" : "h-20"}`}>
          {/* Logo */}
          <button
            onClick={() => { navigate("/"); setTimeout(() => scrollToSection("hero"), 100); }}
            className={`flex items-center transition-all duration-500 ${scrolled ? "h-11" : "h-14"}`}
            data-testid="nav-logo"
          >
            <img
              src={`${R2}/images/9ajhz4vb-freys-media-weiss.png`}
              alt="Freys Media"
              className="h-full w-auto object-contain drop-shadow-lg"
            />
          </button>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            <Pill onClick={() => scrollToSection("hero")}>Startseite</Pill>

            <div onMouseEnter={() => openDropdownDelayed("portfolio")}>
              <Pill active={openDropdown === "portfolio"} hasCaret onClick={() => scrollToSection("cases")}>
                Portfolio
              </Pill>
            </div>

            <div onMouseEnter={() => openDropdownDelayed("drehorte")}>
              <Pill active={openDropdown === "drehorte"} hasCaret onClick={() => scrollToSection("drehorte")}>
                Drehorte
              </Pill>
            </div>

            <div onMouseEnter={() => openDropdownDelayed("services")}>
              <Pill active={openDropdown === "services"} hasCaret onClick={() => scrollToSection("services")}>
                Services
              </Pill>
            </div>

            <Pill onClick={() => { navigate("/ki-loesungen"); closeAll(); }}>KI Lösungen</Pill>
            <Pill onClick={() => scrollToSection("about")}>Über mich</Pill>
            <button
              onClick={() => scrollToSection("contact")}
              className="ml-2 px-5 py-2 rounded-xl bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-white text-sm font-semibold transition-all duration-300 hover:scale-105 neon-glow"
            >
              Kontakt
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 text-foreground hover:text-[hsl(var(--accent))]"
            aria-label={isOpen ? "Menü schließen" : "Menü öffnen"}
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Desktop Dropdown Panel */}
        {openDropdown && (
          <div
            className="hidden md:block absolute left-0 right-0 top-16 bg-background/95 backdrop-blur-xl border-b border-white/5 shadow-2xl"
            onMouseEnter={() => openDropdownDelayed(openDropdown)}
            onMouseLeave={closeDropdownDelayed}
          >
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
              {openDropdown === "portfolio" && (
                <div>
                  <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[hsl(var(--accent))] mb-3">
                    <Briefcase className="w-4 h-4" /> Cases
                  </div>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-1">
                    {casesNav.map((c) => (
                      <ThumbCard key={c.slug} to={`/case/${c.slug}`} title={c.title} poster={c.poster} />
                    ))}
                  </div>
                </div>
              )}
              {openDropdown === "drehorte" && (
                <div>
                  <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[hsl(var(--accent))] mb-3">
                    <MapPin className="w-4 h-4" /> {locations.length} Drehorte
                  </div>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-1">
                    {locations.map((loc) => (
                      <ThumbCard
                        key={loc.id}
                        to={`/drehort/${loc.id}`}
                        title={loc.name}
                        sub={locCategories[loc.category]?.label}
                        poster={loc.poster || `/thumbnails/loc-${loc.id}.webp`}
                      />
                    ))}
                  </div>
                </div>
              )}
              {openDropdown === "services" && (
                <div>
                  <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[hsl(var(--accent))] mb-3">
                    <Film className="w-4 h-4" /> Services
                  </div>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-1">
                    {servicesNav.map((s) => (
                      <ThumbCard key={s.slug} to={`/service/${s.slug}`} title={s.title} poster={s.poster} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden py-4 border-t border-white/5 max-h-[calc(100vh-4rem)] overflow-y-auto">
            <div className="flex flex-col">
              <button onClick={() => scrollToSection("hero")} className="px-3 py-2.5 text-sm font-medium text-left text-muted-foreground hover:text-[hsl(var(--accent))]">Startseite</button>

              {/* Mobile collapsible: Portfolio */}
              <button
                onClick={() => setMobileExpand(mobileExpand === "portfolio" ? null : "portfolio")}
                className="flex items-center justify-between px-3 py-2.5 text-sm font-medium text-left text-muted-foreground hover:text-[hsl(var(--accent))]"
              >
                Portfolio <ChevronDown className={`w-4 h-4 transition-transform ${mobileExpand === "portfolio" ? "rotate-180" : ""}`} />
              </button>
              {mobileExpand === "portfolio" && (
                <div className="pl-4 pb-2 space-y-0">
                  {casesNav.map((c) => (
                    <ThumbCard key={c.slug} to={`/case/${c.slug}`} title={c.title} poster={c.poster} />
                  ))}
                </div>
              )}

              <button
                onClick={() => setMobileExpand(mobileExpand === "drehorte" ? null : "drehorte")}
                className="flex items-center justify-between px-3 py-2.5 text-sm font-medium text-left text-muted-foreground hover:text-[hsl(var(--accent))]"
              >
                Drehorte <ChevronDown className={`w-4 h-4 transition-transform ${mobileExpand === "drehorte" ? "rotate-180" : ""}`} />
              </button>
              {mobileExpand === "drehorte" && (
                <div className="pl-4 pb-2 space-y-0">
                  {locations.map((loc) => (
                    <ThumbCard
                      key={loc.id}
                      to={`/drehort/${loc.id}`}
                      title={loc.name}
                      sub={locCategories[loc.category]?.label}
                      poster={loc.poster || `/thumbnails/loc-${loc.id}.webp`}
                    />
                  ))}
                </div>
              )}

              <button
                onClick={() => setMobileExpand(mobileExpand === "services" ? null : "services")}
                className="flex items-center justify-between px-3 py-2.5 text-sm font-medium text-left text-muted-foreground hover:text-[hsl(var(--accent))]"
              >
                Services <ChevronDown className={`w-4 h-4 transition-transform ${mobileExpand === "services" ? "rotate-180" : ""}`} />
              </button>
              {mobileExpand === "services" && (
                <div className="pl-4 pb-2 space-y-0">
                  {servicesNav.map((s) => (
                    <ThumbCard key={s.slug} to={`/service/${s.slug}`} title={s.title} poster={s.poster} />
                  ))}
                </div>
              )}

              <button onClick={() => { navigate("/ki-loesungen"); closeAll(); }} className="px-3 py-2.5 text-sm font-medium text-left text-muted-foreground hover:text-[hsl(var(--accent))]">
                KI Lösungen
              </button>
              <button onClick={() => scrollToSection("about")} className="px-3 py-2.5 text-sm font-medium text-left text-muted-foreground hover:text-[hsl(var(--accent))]">Über mich</button>
              <button onClick={() => scrollToSection("contact")} className="px-3 py-2.5 text-sm font-medium text-left text-muted-foreground hover:text-[hsl(var(--accent))]">Kontakt</button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
